import { cn } from "../../../utils/cn";

interface Conversation {
  id: string;
  name: string;
  lastMessage: string;
  lastMessageTimestamp: string | number;
  unreadCount: number;
  profilePic?: string;
  status?: string;
  user?: any; // ✅ full sender object
}

interface Props {
  conversation?: Conversation;
  isSelected?: boolean;
  isLast?: boolean;
  onSelect?: (id: string) => void;
  loading?: boolean;
}

export const ConversationListItem: React.FC<Props> = ({
  conversation,
  isSelected = false,
  isLast = false,
  onSelect = () => {},
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="flex items-center gap-4 p-4 py-6 w-full border-t border-gray-300 animate-pulse">
        <div className="w-10 h-10 rounded-[4px] bg-gray-300" />
        <div className="flex-1 space-y-2">
          <div className="flex justify-between">
            <div className="w-24 h-3 bg-gray-300 rounded-md" />
            <div className="w-10 h-3 bg-gray-300 rounded-md" />
          </div>
          <div className="w-36 h-3 bg-gray-300 rounded-md" />
        </div>
      </div>
    );
  }

  if (!conversation) return null;
  return (
    <div
      onClick={() => onSelect(conversation.id)}
      className={cn(
        "flex items-center gap-4 w-full cursor-pointer transition-all rounded-[16px] my-2 px-4 py-3",
        isSelected
          ? "bg-[#E9EFFC] border-l-4 border-[#143887CC]"
          : "hover:bg-[#E9EFFC] hover:border-l-4 hover:border-[#143887CC]"
      )}
    >
      {/* Avatar */}
      {conversation.profilePic ? (
        <div className="relative ">
          <img
            src={conversation.profilePic}
            alt={conversation.name}
            className="w-10 h-10 object-cover rounded-full border border-[#14388733]"
          />
          <span
            className={cn(
              "w-[8px] h-[8px] rounded-full absolute -right-1 -bottom-1 border border-white",
              conversation?.user.status?.toLowerCase() === "active"
                ? "bg-[#27AE60]"
                : "bg-gray-400"
            )}
          />
        </div>
      ) : (
        <div className="w-10 h-10 rounded-full bg-[#E9EFFC] flex items-center justify-center text-[#143887] font-bold border border-[#14388733]">
          {conversation.name?.[0] || "?"}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center">
          <h3
            className={cn(
              "text-sm truncate",
              conversation.unreadCount > 0 && !isSelected
                ? "font-semibold text-black"
                : "font-medium text-[#0D160B]"
            )}
          >
            {conversation.name}
          </h3>

          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500 whitespace-nowrap">
              {conversation.lastMessageTimestamp}
            </span>

            {/* Unread badge */}
            {conversation.unreadCount > 0 && !isSelected && (
              <span className="min-w-[18px] h-[18px] flex items-center justify-center text-[10px] bg-[#143887] text-white rounded-full px-1.5">
                {conversation.unreadCount > 9 ? "9+" : conversation.unreadCount}
              </span>
            )}
          </div>
        </div>

        <p
          className={cn(
            "text-xs truncate mt-1",
            conversation.unreadCount > 0 && !isSelected
              ? "text-black font-medium"
              : "text-gray-500"
          )}
        >
          {conversation.lastMessage}
        </p>
      </div>
    </div>
  );
};
